import { z } from 'zod';

/**
 * Atlas settings developers may change from the panel (PRD "Developer
 * settings"). The names are Atlas's environment variables. Secrets that link
 * the panel, Atlas and Canvas-AI together (WEBHOOK_SECRET, CANVAS_INTERNAL_SECRET,
 * ATLAS_SOURCE_SECRET, CF_API_TOKEN, RESEND_API_KEY) are deliberately absent:
 * rotating one of those from here could cut the panel off from Atlas.
 */
export const ATLAS_SETTINGS = [
  { name: 'SERPAPI_API_KEY', label: 'SerpAPI key', group: 'Search', secret: true },
  { name: 'BRIGHTDATA_API_KEY', label: 'Bright Data key', group: 'Search', secret: true },
  { name: 'SERP_PROVIDER', label: 'Search provider', group: 'Search', secret: false },
  { name: 'OPENROUTER_API_KEY', label: 'OpenRouter key', group: 'Model providers', secret: true },
  { name: 'OPENROUTER_MODEL', label: 'OpenRouter model', group: 'Model providers', secret: false },
  { name: 'MINIMAX_API_KEY', label: 'MiniMax key', group: 'Model providers', secret: true },
  { name: 'HETZNER_API_KEY', label: 'Hetzner key', group: 'Model providers', secret: true },
  { name: 'HETZNER_MODEL', label: 'Hetzner model', group: 'Model providers', secret: false },
  { name: 'PRIMARY_MODEL', label: 'Primary model', group: 'Model providers', secret: false },
] as const;

export type AtlasSettingName = (typeof ATLAS_SETTINGS)[number]['name'];
export const ATLAS_SETTING_NAMES = ATLAS_SETTINGS.map((s) => s.name) as [
  AtlasSettingName,
  ...AtlasSettingName[],
];

export interface SettingView {
  name: AtlasSettingName;
  label: string;
  group: string;
  secret: boolean;
  /** Set in the panel at all. */
  isSet: boolean;
  /** Plain settings: the value. Secrets: only the last four characters. */
  value: string | null;
  last4: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; name: string } | null;
  pushedToAtlasAt: string | null;
  pushError: string | null;
}

export const UpdateSettingSchema = z.object({
  value: z
    .string()
    .trim()
    .min(1)
    .max(4000)
    .refine((v) => !/[\r\n]/.test(v), 'Must be a single line'),
});

export interface SystemInfo {
  appVersion: string;
  migrations: number;
  atlas: {
    reachable: boolean;
    baseUrl: string;
    capabilities: Record<string, boolean> | null;
  };
  jobs: { name: string; queued: number; active: number; failed: number }[];
}
